import { useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import moment from 'moment';
import { intervalSelector, filterSelector } from '@store/overviewDashboard/base/selectors';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { getMetricOvbShortName } from '@utils/metrics/metricHelpers';
import { buildMainHeader, downloadCsv } from '@components/export/buildCsv';
import { formatCell, TS_FORMAT } from '@components/export/formatters';
import { getCustomerInfo } from '@src/globalStore/profileStore';
import { ALL_TRAFFIC } from '@src/utils/constants';
import { useDimensionMetrics } from './useDimensionMetrics';
import { useBenchmarkMetrics } from './useBenchmarkMetrics';

interface ExportParams {
  dimensions: string[];
  metrics: string[];
  benchmarkType: 'good' | 'best';
  percentile: number;
}

interface DimensionData {
  dimension: string;
  dimensionName: string;
  data: any[];
  benchmarkData: any[];
  adjustedInterval?: string;
}

const useBenchmarkExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const interval = useSelector(intervalSelector);
  const dimensionMeta = useSelector(dimensionsListSelectorKeyValue);
  const appliedFilter = useSelector(filterSelector);

  const { fetchDimensionMetrics } = useDimensionMetrics();
  const { fetchBenchmarkMetrics } = useBenchmarkMetrics();

  const exportToCSV = (
    data: DimensionData[],
    fileName: string,
    benchmarkType: 'good' | 'best',
    percentile: number,
    adjustedInterval?: string,
  ) => {
    const { customerName } = getCustomerInfo();
    const csvParts: string[] = [];

    const filterName = get(appliedFilter, 'name', ALL_TRAFFIC);

    csvParts.push(buildMainHeader(customerName));
    csvParts.push('Benchmark Export');
    csvParts.push(`Filter: ${filterName}`);
    csvParts.push(
      `Interval: ${moment(interval.from).format(TS_FORMAT)} to ${moment(interval.to).format(TS_FORMAT)}`,
    );
    csvParts.push(`Benchmark Type: Conviva ${benchmarkType === 'best' ? 'Best' : 'Good'}`);
    csvParts.push(`Percentile: P${percentile}`);

    // Add benchmark month detail if available
    if (adjustedInterval) {
      csvParts.push(`Benchmark Duration: ${adjustedInterval}`);
    }

    csvParts.push('\r\n');

    data.forEach((dimensionData) => {
      const { dimension, dimensionName, data: dimData, benchmarkData } = dimensionData;

      if (!dimData || dimData.length === 0) {
        return;
      }

      csvParts.push(dimensionName);

      const firstRow = dimData[0];
      const excludeFields = [
        dimension,
        'name',
        'value',
        'latitude',
        'longitude',
        'lat',
        'lon',
        'coordinates',
      ];
      const metricColumns = Object.keys(firstRow).filter(
        (key) => !excludeFields.includes(key) && !key.toLowerCase().includes('coordinate'),
      );

      const headers: Array<{ key: string; title: string; isBenchmark?: boolean }> = [
        { key: 'name', title: 'Name' },
      ];

      // Add regular metrics and their benchmark counterparts side by side
      metricColumns.forEach((metricKey) => {
        const displayName = getMetricOvbShortName({ metricKey, defaultValue: metricKey });
        headers.push({ key: metricKey, title: displayName });

        // Add benchmark column for this metric if benchmark data exists
        if (benchmarkData && benchmarkData.length > 0) {
          headers.push({
            key: `benchmark.${metricKey}`,
            title: `${displayName} (Benchmark)`,
            isBenchmark: true,
          });
        }
      });

      const rows: any[] = [];

      dimData.forEach((row) => {
        const exportRow: any = {};
        exportRow.name = row.name || row[dimension] || 'N/A';

        const benchmarkRow =
          benchmarkData && benchmarkData.length > 0
            ? benchmarkData.find((br) => br[dimension] === row[dimension])
            : null;

        metricColumns.forEach((metricKey) => {
          exportRow[metricKey] = row[metricKey];

          if (benchmarkData && benchmarkData.length > 0) {
            const benchmarkValue = benchmarkRow ? benchmarkRow[metricKey] : null;
            exportRow[`benchmark.${metricKey}`] =
              benchmarkValue !== null && benchmarkValue !== undefined ? benchmarkValue : '-';
          }
        });

        rows.push(exportRow);
      });

      const csvRows = rows.map((row) => {
        return headers.map((header) => formatCell(header, row[header.key])).join(',');
      });

      csvParts.push(headers.map((h) => h.title).join(','));
      csvParts.push(...csvRows);
      csvParts.push('\r\n');
    });

    downloadCsv(csvParts.join('\r\n'), fileName);
  };

  const exportBenchmarkData = async ({
    dimensions,
    metrics,
    benchmarkType,
    percentile,
  }: ExportParams) => {
    setIsExporting(true);
    setProgress(0);
    setError(null);

    try {
      const allDimensionData: DimensionData[] = [];
      const totalDimensions = dimensions.length;
      let benchmarkAdjustedInterval: string | undefined;

      const filterRules = get(appliedFilter, 'rule', []);
      const hasCountryInFilter = filterRules.some((ruleGroup: any[]) =>
        ruleGroup.some((rule: any) => rule.field === 'GEO_COUNTRY'),
      );

      for (let i = 0; i < dimensions.length; i++) {
        const dimension = dimensions[i];
        setProgress(Math.round(((i + 1) / totalDimensions) * 100));

        try {
          const dimensionResponse = await fetchDimensionMetrics({
            dimension,
            metrics,
          });

          const dimensionData = dimensionResponse.data || [];
          let benchmarkData: any[] = [];

          const shouldFetchBenchmark = hasCountryInFilter || dimension === 'GEO_COUNTRY';

          if (shouldFetchBenchmark) {
            const benchmarkResponse = await fetchBenchmarkMetrics({
              dimension,
              metrics,
              benchmarkType,
              percentile,
            });

            benchmarkData = benchmarkResponse.data || [];

            // Capture adjustedInterval from the first benchmark response
            if (!benchmarkAdjustedInterval && benchmarkResponse.adjustedInterval) {
              benchmarkAdjustedInterval = benchmarkResponse.adjustedInterval;
            }
          }

          const dimensionName = (dimensionMeta as Record<string, string>)[dimension] || dimension;

          allDimensionData.push({
            dimension,
            dimensionName,
            data: dimensionData,
            benchmarkData,
            adjustedInterval: benchmarkAdjustedInterval,
          });
        } catch {
          allDimensionData.push({
            dimension,
            dimensionName: dimension,
            data: [],
            benchmarkData: [],
          });
        }
      }

      exportToCSV(
        allDimensionData,
        'Benchmark_Export',
        benchmarkType,
        percentile,
        benchmarkAdjustedInterval,
      );

      setIsExporting(false);
      setProgress(100);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export benchmark data';
      setError(errorMessage);
      setIsExporting(false);
      setProgress(0);

      return { success: false, error: errorMessage };
    }
  };

  return {
    exportBenchmarkData,
    isExporting,
    progress,
    error,
  };
};

export default useBenchmarkExport;
